import React, { useState, useEffect } from 'react';
import { 
  X, 
  Plus, 
  Trash2, 
  Package, 
  Search, 
  AlertCircle, 
  CheckCircle,
  ShoppingCart
} from 'lucide-react';
import { getProductImageUrl as getConfigProductImageUrl, API_BASE_URL } from '../config';

const CategoryProductsModal = ({ isOpen, onClose, category, onProductsUpdated }) => {
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'add'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Get product image URL
  const getProductImageUrl = (product) => {
    return getConfigProductImageUrl(product);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch current products in category
  const fetchCategoryProducts = async () => {
    if (!category) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/category/${category.category_id}/products`, {
        headers: getAuthHeaders(),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setCategoryProducts(data.products || []);
      } else {
        setError(data.error || 'Failed to fetch category products');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error fetching category products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available products (not in category)
  const fetchAvailableProducts = async () => {
    if (!category) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/get-available-products/${category.category_id}`, {
        headers: getAuthHeaders(),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setAvailableProducts(data.products || []);
      } else {
        setError(data.error || 'Failed to fetch available products');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error fetching available products:', err);
    }
  };

  // Remove product from category
  const removeProductFromCategory = async (productId) => {
    if (!window.confirm('Remove this product from the category?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/category/${category.category_id}/remove-product`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ product_id: productId }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Product removed from category');
        setCategoryProducts(prev => prev.filter(p => p.product_id !== productId));
        fetchAvailableProducts(); // Refresh available products
        
        if (onProductsUpdated) {
          onProductsUpdated();
        }
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to remove product');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error removing product:', err);
    }
  };

  // Add single product to category
  const addProductToCategory = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/category/${category.category_id}/add-product`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ product_id: productId }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Product added to category');
        fetchCategoryProducts(); // Refresh category products
        setAvailableProducts(prev => prev.filter(p => p.product_id !== productId));
        
        if (onProductsUpdated) {
          onProductsUpdated();
        }
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to add product');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error adding product:', err);
    }
  };

  // Bulk add selected products
  const bulkAddProducts = async () => {
    if (selectedProducts.length === 0) {
      setError('Please select products to add');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/category/${category.category_id}/bulk-add-products`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ product_ids: selectedProducts }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`${data.added_count} products added to category`);
        setSelectedProducts([]);
        fetchCategoryProducts();
        fetchAvailableProducts();
        
        if (onProductsUpdated) {
          onProductsUpdated();
        }
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to add products');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error bulk adding products:', err);
    }
  };

  // Handle product selection for bulk add
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Filter products based on search
  const filteredCategoryProducts = categoryProducts.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAvailableProducts = availableProducts.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && category) {
      fetchCategoryProducts();
      fetchAvailableProducts();
      setActiveTab('current');
      setSearchTerm('');
      setSelectedProducts([]);
      setError('');
      setSuccess('');
    }
  }, [isOpen, category]);

  if (!isOpen || !category) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage Products</h2>
            <p className="text-sm text-gray-600">{category.category_name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              onClick={() => setActiveTab('current')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'current'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Package className="h-4 w-4" />
                <span>Current Products ({categoryProducts.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('add')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'add'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Add Products ({availableProducts.length})</span>
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Messages */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-green-700 text-sm">{success}</span>
            </div>
          )}

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Current Products Tab */}
          {activeTab === 'current' && (
            <div>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading products...</p>
                </div>
              ) : filteredCategoryProducts.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {filteredCategoryProducts.map((product) => (
                    <div key={product.product_id} className="bg-gray-50 rounded-lg p-4 flex items-center space-x-4">
                      {/* Product Image */}
                      <div className="flex-shrink-0 w-16 h-16">
                        <img
                          src={getProductImageUrl(product)}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 bg-gray-200 rounded-lg hidden items-center justify-center border">
                          <Package className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Product Details */}
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{product.name}</h4>
                        <div className="text-sm text-gray-600">
                          {product.sale_price ? (
                            <span>₹{product.sale_price} <span className="line-through">₹{product.base_price}</span></span>
                          ) : (
                            <span>₹{product.base_price}</span>
                          )}
                          <span className="ml-2">• {product.quantity} in stock</span>
                        </div>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                          product.status === 'active' ? 'bg-green-100 text-green-800' :
                          product.status === 'inactive' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {product.status}
                        </span>
                      </div>
                      
                      {/* Remove Button */}
                      <button
                        onClick={() => removeProductFromCategory(product.product_id)}
                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                        title="Remove from category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No products in this category</h3>
                  <p className="text-gray-600 mb-4">Add products to get started</p>
                  <button
                    onClick={() => setActiveTab('add')}
                    className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Products</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Add Products Tab */}
          {activeTab === 'add' && (
            <div>
              {/* Bulk Actions */}
              {selectedProducts.length > 0 && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center justify-between">
                  <span className="text-blue-700 text-sm">
                    {selectedProducts.length} product(s) selected
                  </span>
                  <button
                    onClick={bulkAddProducts}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    Add Selected
                  </button>
                </div>
              )}

              {filteredAvailableProducts.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {filteredAvailableProducts.map((product) => (
                    <div key={product.product_id} className="bg-gray-50 rounded-lg p-4 flex items-center space-x-4">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.product_id)}
                        onChange={() => toggleProductSelection(product.product_id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                      />
                      
                      {/* Product Image */}
                      <div className="flex-shrink-0 w-16 h-16">
                        <img
                          src={getProductImageUrl(product)}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 bg-gray-200 rounded-lg hidden items-center justify-center border">
                          <Package className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Product Details */}
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{product.name}</h4>
                        <div className="text-sm text-gray-600">
                          {product.sale_price ? (
                            <span>₹{product.sale_price} <span className="line-through">₹{product.base_price}</span></span>
                          ) : (
                            <span>₹{product.base_price}</span>
                          )}
                        </div>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                          product.status === 'active' ? 'bg-green-100 text-green-800' :
                          product.status === 'inactive' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {product.status}
                        </span>
                      </div>
                      
                      {/* Add Button */}
                      <button
                        onClick={() => addProductToCategory(product.product_id)}
                        className="flex-shrink-0 p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                        title="Add to category"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No available products</h3>
                  <p className="text-gray-600">All products are already in this category or no products match your search.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Category: <span className="font-medium">{category.category_name}</span> • 
              <span className="ml-1">{categoryProducts.length} products</span>
            </div>
            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryProductsModal;